<script lang="ts">
	import { Logo } from '@icons';
	import { menu } from '@tools/Menu.svelte';
	import type { GlobalSettingsType } from '$lib/queries/GlobalSettingsQuery';
	import { Breakpoints } from '@tools';
	import { navigating } from '$app/stores';
	import ChevronDown from '@icons/ChevronDown.svelte';
	import MenuBurgerButton from './menu/MenuBurgerButton.svelte';
	import SearchInput from '@atoms/SearchInput.svelte';

	let {
		menus,
		searchQuery = $bindable(''),
		searchResults = $bindable([]),
		searchLoading = $bindable(false)
	}: {
		menus: GlobalSettingsType['menus'];
		searchQuery?: string;
		searchResults?: any[];
		searchLoading?: boolean;
	} = $props();

	let collapsedSearch = $state(true);

	$effect(() => {
		const unsub = navigating.subscribe((data) => {
			if (!data) {
				menu.close();
			}
		});

		return () => {
			unsub();
		};
	});

	$effect(() => {
		if (Breakpoints.md && menu.isMobile) menu.close();
	});
</script>

<nav class:searching={!collapsedSearch}>
	<a href="/" class="logo" title="Hjem">
		<Logo />
	</a>
	<SearchInput
		collapsible={true}
		bind:collapsed={collapsedSearch}
		bind:query={searchQuery}
		bind:results={searchResults}
		bind:loading={searchLoading}
	/>
	<ul>
		{#if Breakpoints.tn}
			<li class="kalender"><a class="item" href="/kalender"><span>Kalender</span></a></li>
		{/if}
		{#if Breakpoints.md}
			{#each menus as submenu}
				<li>
					<button
						class="item"
						aria-pressed={menu.current === submenu.id}
						onclick={() => menu.click(submenu.id)}
						><span>{submenu.title}</span><ChevronDown /></button
					>
				</li>
			{/each}
		{:else}
			<li class="burger">
				<MenuBurgerButton />
			</li>
		{/if}
	</ul>
</nav>

<style lang="scss">
	nav {
		z-index: 25;
		position: fixed;
		top: 0;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: end;
		background-color: $colorNavBackground;
		height: var(--nav-height);

		:global(.SearchInput) {
			background-color: $colorNavForeground;
			color: $colorNavBackground;
			margin-left: $gap4;
		}

		:global(.SearchInput ::selection) {
			background-color: $colorNavBackground;
			color: $colorNavForeground;
		}

		@include sm {
			:global(.SearchInput input) {
				width: 18em;
				max-width: 18em;
			}
		}

		:global(.SearchInput input::placeholder) {
			color: $colorNavBackground;
			opacity: 1;
			transition: opacity $transition;
		}

		&.searching {
			.logo,
			.burger {
				display: none;

				@include tn {
					display: grid;
				}
			}

			:global(.SearchInput) {
				flex: 1 1 auto;

				@include sm {
					flex: unset;
				}
			}

			.kalender {
				display: none;

				@include sm {
					display: flex;
				}
			}
		}
	}

	.logo {
		display: grid;
		aspect-ratio: 1 / 1;
		background-color: $colorNavForeground;
		color: $colorBackground;
		place-items: center;
		padding: 1rem;
		margin-right: auto;
		width: var(--nav-height);
		height: var(--nav-height);
		flex: 0 0 var(--nav-height);

		:global(svg) {
			transition: transform $transition;
		}

		&:hover {
			:global(svg) {
				transform: scale(1.08);
			}
		}
	}

	ul {
		display: flex;
		align-items: stretch;
		padding-inline-start: 0;
		margin-block: 0;
		list-style-type: none;
		height: 100%;

		@include md {
			padding-right: calc(var(--gutter) - $gap4 * 0.5);
			padding-left: calc($gap4 * 0.5);
		}

		:global(svg) {
			@include md {
				height: 1.2em;
				width: 1.2em;
				position: relative;
				z-index: 2;
			}
		}
	}

	li {
		display: flex;
		justify-content: center;
		align-items: stretch;
	}

	.item {
		padding-right: $buttonPaddingHorizontal;
		padding-left: calc($buttonPaddingHorizontal + $gap4 * 0.5);
		position: relative;
		display: flex;
		gap: $gap2;
		align-items: center;
		line-height: $gap5;
		text-decoration: none;

		@include md {
			padding-right: calc($buttonPaddingHorizontal + $gap4 * 0.5);
		}

		span {
			position: relative;
			z-index: 2;
		}

		&::before {
			content: '';
			position: absolute;
			left: calc($gap4 * 0.5);
			right: 0;
			top: 50%;
			transform: translateY(-50%);
			z-index: 1;
			height: calc($gap5 + $gap2 * 2);
			border-radius: calc($gap5 * 0.5 + $gap2);
			background-color: transparent;
			transition: background-color $transition;

			@include md {
				right: calc($gap4 * 0.5);
			}
		}

		&:hover {
			&::before {
				background-color: $colorNavHover;
			}
		}

		&:focus-visible {
			outline: none;

			&::before {
				box-shadow: 0 0 0 2px $colorNavForeground;
			}
		}

		:global(svg) {
			width: 0.625em;
			height: $gap5;
			transition: transform $transition;
		}

		&[aria-pressed='true'] {
			color: $colorNavBackground;

			&::before {
				background-color: $colorNavForeground;
			}

			:global(svg) {
				transform: rotate(180deg);
			}
		}
	}
</style>
